import { type ReactNode, useState } from "react";
import { type BrewSystem, type BrewSystemInput, send, useChange } from "../api";
import { Dialog, DialogForm, ErrorNote, Field } from "../components/ui";
import { useNotify } from "../notify";

/**
 * A recipe's batch settings, and the brew systems that fill them (Scott, 2026-10-02): "build these into
 * a brew system so i can prebuild and just select this as a 15 bbl batch on my system. I could then
 * edit this on the recipe still if i needed to without effecting the default brew system specs".
 * Picking a system copies its figures into the recipe, so the two never change each other after.
 * Carbonation is the beer's, not the brewhouse's, so it is on the recipe only (Scott's Jam, 2026-10-02:
 * "carbonation does not need to be a part of the brew system but does need to be a fillable in the
 * recipe").
 */

const LITERS_PER_GALLON = 3.785411784;
const toF = (c: number) => (c * 9) / 5 + 32;
const toC = (f: number) => ((f - 32) * 5) / 9;
const num = (s: string) => (s.trim() === "" ? Number.NaN : Number(s));
const shown = (n: number, places: number) => String(Number(n.toFixed(places)));
/** A number as the API stores it: a decimal string, no float noise past six places. */
const exact = (n: number) => String(Number(n.toFixed(6)));

/** The Batch section's figures as typed: gallons, minutes, percent, feet and °F. */
export interface BatchFigures {
  batchGal: string;
  boilMinutes: string;
  efficiencyPct: string;
  boilOffGal: string;
  trubGal: string;
  coolingLossPct: string;
  altitudeFeet: string;
  mashTempF: string;
  mashMinutes: string;
}

export const BATCH_FIELDS: { key: keyof BatchFigures; label: string; unit: string }[] = [
  { key: "batchGal", label: "Batch", unit: "gal" },
  { key: "boilMinutes", label: "Boil", unit: "min" },
  { key: "efficiencyPct", label: "Efficiency", unit: "%" },
  { key: "boilOffGal", label: "Boil-off", unit: "gal" },
  { key: "trubGal", label: "Trub loss", unit: "gal" },
  { key: "coolingLossPct", label: "Cooling loss", unit: "%" },
  { key: "altitudeFeet", label: "Altitude", unit: "ft" },
  { key: "mashTempF", label: "Mash", unit: "°F" },
  { key: "mashMinutes", label: "Mash time", unit: "min" },
];

export const BLANK_FIGURES: BatchFigures = {
  batchGal: "",
  boilMinutes: "60",
  efficiencyPct: "75",
  boilOffGal: "0",
  trubGal: "0",
  coolingLossPct: "4",
  altitudeFeet: "0",
  mashTempF: "152",
  mashMinutes: "60",
};

const maybe = (v: string | null, show: (n: number) => string) => (v == null ? "" : show(Number(v)));

/**
 * A system's figures as the builder shows them, to the same places. A mash the system leaves empty is
 * "".
 */
export function figuresOf(s: BrewSystem): BatchFigures {
  return {
    batchGal: shown(Number(s.batchLiters) / LITERS_PER_GALLON, 2),
    boilMinutes: shown(Number(s.boilMinutes), 0),
    efficiencyPct: shown(Number(s.efficiencyPct), 1),
    boilOffGal: shown(Number(s.boilOffLiters) / LITERS_PER_GALLON, 2),
    trubGal: shown(Number(s.trubLossLiters) / LITERS_PER_GALLON, 2),
    coolingLossPct: shown(Number(s.coolingLossPct), 1),
    altitudeFeet: shown(Number(s.altitudeFeet), 0),
    mashTempF: maybe(s.mashTempC, (c) => shown(toF(c), 1)),
    mashMinutes: maybe(s.mashMinutes, (m) => shown(m, 0)),
  };
}

/** What picking a system makes the figures: everything it says, and the recipe's own where it is silent. */
export function pickedFrom(s: BrewSystem, own: BatchFigures): BatchFigures {
  const theirs = figuresOf(s);
  const kept = Object.entries(theirs).map(([k, v]) => [
    k,
    v === "" ? own[k as keyof BatchFigures] : v,
  ]);
  return Object.fromEntries(kept) as unknown as BatchFigures;
}

/** Whether a figure as typed is not the system's. A figure the system leaves empty is never off. */
export function differs(typed: string, theirs: string): boolean {
  return theirs !== "" && num(typed) !== num(theirs);
}

/** The figures as the API takes them: liters, °C, and empty optional figures as null. */
export function systemInputOf(name: string, f: BatchFigures): BrewSystemInput {
  const or0 = (v: string) => (Number.isFinite(num(v)) ? num(v) : 0);
  const optional = (v: string, to = (n: number) => n) =>
    Number.isFinite(num(v)) ? exact(to(num(v))) : null;
  return {
    name: name.trim(),
    batchLiters: exact(or0(f.batchGal) * LITERS_PER_GALLON),
    boilMinutes: exact(or0(f.boilMinutes)),
    efficiencyPct: exact(or0(f.efficiencyPct)),
    boilOffLiters: exact(or0(f.boilOffGal) * LITERS_PER_GALLON),
    trubLossLiters: exact(or0(f.trubGal) * LITERS_PER_GALLON),
    coolingLossPct: exact(or0(f.coolingLossPct)),
    altitudeFeet: exact(or0(f.altitudeFeet)),
    mashTempC: optional(f.mashTempF, toC),
    mashMinutes: optional(f.mashMinutes),
  };
}

/** One line on a system, for a list: its batch, boil, efficiency and altitude. */
export function systemSummary(s: BrewSystem): string {
  const f = figuresOf(s);
  return [
    `${Number(f.batchGal).toLocaleString("en-US")} gal batch`,
    `${f.boilMinutes} min boil`,
    `${f.efficiencyPct}% efficiency`,
    `${f.boilOffGal} gal boil-off`,
    `${Number(f.altitudeFeet).toLocaleString("en-US")} ft`,
  ].join(", ");
}

/** What a system still needs before it can be saved. */
function problemsOf(name: string, f: BatchFigures): string[] {
  return [
    ...(name.trim() ? [] : ["a name"]),
    ...(num(f.batchGal) > 0 ? [] : ["a batch size"]),
    ...(Number.isFinite(num(f.boilMinutes)) ? [] : ["a boil time"]),
    ...(Number.isFinite(num(f.efficiencyPct)) ? [] : ["an efficiency"]),
  ];
}

/**
 * A figure, with the one worked out in gray until one is typed. Clicking into the gray figure makes it
 * the box's text, selected, so it can be typed over or changed a little (Scott's Jam, 2026-10-08:
 * "Attenuation should be editable"); leaving it as it was, or emptied, keeps it worked out.
 */
export function NumberField({
  label,
  unit,
  value,
  hint,
  placeholder,
  onChange,
}: {
  label: string;
  unit: string;
  value: string;
  hint?: ReactNode;
  /** A figure worked out rather than typed, shown in gray until one is typed. */
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  // The worked-out figure while it is being looked at, before anything is typed; null otherwise.
  const [worked, setWorked] = useState<string | null>(null);
  const [keepSelection, setKeepSelection] = useState(false);
  return (
    <Field label={label} hint={hint}>
      <span className="input-unit">
        <input
          className="input num"
          inputMode="decimal"
          aria-label={label}
          value={worked ?? value}
          placeholder={placeholder}
          onFocus={(e) => {
            if (value !== "" || !placeholder) return;
            const input = e.currentTarget;
            setWorked(placeholder);
            setKeepSelection(true);
            requestAnimationFrame(() => input.select());
          }}
          // A click's mouseup would otherwise drop the selection just made.
          onMouseUp={(e) => {
            if (!keepSelection) return;
            e.preventDefault();
            setKeepSelection(false);
          }}
          onBlur={() => {
            setWorked(null);
            setKeepSelection(false);
          }}
          onChange={(e) => {
            const typed = e.target.value.replace(/[^\d.]/g, "");
            setKeepSelection(false);
            if (worked !== null && (typed === "" || typed === worked)) {
              setWorked(typed);
              return;
            }
            setWorked(null);
            onChange(typed);
          }}
        />
        {unit ? <span className="unit">{unit}</span> : null}
      </span>
    </Field>
  );
}

/**
 * The nine figures. With a system, each one typed differently from the system's says so under it, with
 * the system's figure to put back.
 */
export function BatchFields({
  value,
  onChange,
  system,
}: {
  value: BatchFigures;
  onChange: (patch: Partial<BatchFigures>) => void;
  system?: { name: string; figures: BatchFigures } | null;
}) {
  return (
    <div className="form-grid builder-settings">
      {BATCH_FIELDS.map((f) => {
        const theirs = system?.figures[f.key] ?? "";
        const off = system ? differs(value[f.key], theirs) : false;
        return (
          <NumberField
            key={f.key}
            label={f.label}
            unit={f.unit}
            value={value[f.key]}
            onChange={(v) => onChange({ [f.key]: v })}
            hint={
              off && system ? (
                <span className="system-differs">
                  {system.name}: {theirs} {f.unit}{" "}
                  <button
                    type="button"
                    className="link-button"
                    aria-label={`Put back ${system.name}'s ${f.label.toLowerCase()}, ${theirs} ${f.unit}`}
                    onClick={() => onChange({ [f.key]: theirs })}
                  >
                    Put back
                  </button>
                </span>
              ) : undefined
            }
          />
        );
      })}
    </div>
  );
}

/**
 * Naming a new brew system, or changing one. From the recipe builder it is named from the Batch figures
 * already typed, so only the name is asked; from Settings every figure is there to set.
 */
export function BrewSystemDialog({
  open,
  onClose,
  system,
  from,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** The system being changed; none for a new one. */
  system?: BrewSystem;
  /** A recipe's figures to name as a system, as they are. */
  from?: BatchFigures;
  onSaved?: (saved: BrewSystem) => void;
}) {
  const notify = useNotify();
  const [name, setName] = useState(system?.name ?? "");
  const [figures, setFigures] = useState<BatchFigures>(
    system ? figuresOf(system) : (from ?? BLANK_FIGURES),
  );
  const [tried, setTried] = useState(false);
  const shownFigures = from ?? figures;
  const problems = problemsOf(name, shownFigures);
  const save = useChange(
    (body: BrewSystemInput, key) =>
      system ? send.saveBrewSystem({ id: system.id, body }, key) : send.createBrewSystem(body, key),
    {
      onSuccess: (saved) => {
        notify(system ? `${saved.name} saved` : `${saved.name} added to your brew systems`);
        onSaved?.(saved);
        onClose();
      },
    },
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={system ? `Change ${system.name}` : "New brew system"}
      wide={!from}
    >
      <DialogForm
        onSubmit={() => {
          setTried(true);
          if (problems.length === 0) save.mutate(systemInputOf(name, shownFigures));
        }}
        footer={
          <>
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="button primary" disabled={save.isPending}>
              {system ? "Save" : "Add brew system"}
            </button>
          </>
        }
      >
        <Field label="Name" hint={from ? undefined : "What your brewers call it, like 15 bbl"}>
          <input
            className="input"
            value={name}
            placeholder={from ? "15 bbl" : undefined}
            onChange={(e) => setName(e.target.value)}
            autoFocus={Boolean(from)}
          />
        </Field>
        {from ? (
          <p className="muted" style={{ margin: 0 }}>
            Saves this recipe's Batch figures under that name, for any recipe to pick. Changing them
            on a recipe later never changes the system.
          </p>
        ) : (
          <>
            <BatchFields value={figures} onChange={(p) => setFigures((f) => ({ ...f, ...p }))} />
            <p className="muted" style={{ margin: 0 }}>
              Leave Mash or Mash time empty to keep each recipe's own.{" "}
              {system && system.recipes > 0
                ? `The ${system.recipes === 1 ? "recipe" : `${system.recipes} recipes`} already using it keep the figures they have.`
                : ""}
            </p>
          </>
        )}
        {tried && problems.length ? (
          <div className="form-error">Still needs {problems.join(", ")}.</div>
        ) : null}
        <ErrorNote error={save.error} />
      </DialogForm>
    </Dialog>
  );
}
